import { createPortal } from "react-dom";

const PriceChart = ({ data }) => {
  if (data.length < 2)
    return (
      <p
        style={{
          fontSize: "0.875rem",
          color: "var(--text-secondary)",
          textAlign: "center",
        }}
      >
        Need at least 2 data points to show a chart.
      </p>
    );

  const prices = data.map((d) => d.price);
  const minP = Math.min(...prices);
  const maxP = Math.max(...prices);
  const range = maxP - minP || 1;
  const W = 600,
    H = 180,
    PAD = 44;
  const innerW = W - PAD * 2;
  const innerH = H - PAD * 2;

  const points = data.map((d, i) => ({
    x: PAD + (i / (data.length - 1)) * innerW,
    y: PAD + innerH - ((d.price - minP) / range) * innerH,
    price: d.price,
    date: d.created_at?.toDate ? d.created_at.toDate() : new Date(d.created_at),
  }));

  const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");
  const areaPath =
    `M${points[0].x},${PAD + innerH} ` +
    points.map((p) => `L${p.x},${p.y}`).join(" ") +
    ` L${points[points.length - 1].x},${PAD + innerH} Z`;

  return (
    <div>
      <p
        style={{
          fontSize: "0.875rem",
          color: "var(--text-secondary)",
          marginBottom: "0.5rem",
        }}
      >
        Price trend over time
      </p>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        style={{
          width: "100%",
          height: "auto",
          background: "var(--bg-secondary)",
          borderRadius: "0.5rem",
        }}
      >
        {[0, 0.25, 0.5, 0.75, 1].map((t) => {
          const y = PAD + innerH - t * innerH;
          const val = (minP + t * range).toFixed(1);
          return (
            <g key={t}>
              <line
                x1={PAD}
                y1={y}
                x2={PAD + innerW}
                y2={y}
                stroke="var(--border-primary)"
                strokeWidth="1"
              />
              <text
                x={PAD - 4}
                y={y + 4}
                textAnchor="end"
                fontSize="10"
                fill="var(--text-secondary)"
              >
                {val}
              </text>
            </g>
          );
        })}
        <path d={areaPath} fill="var(--color-primary)" fillOpacity="0.12" />
        <polyline
          points={polyline}
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth="2.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="4"
            fill="var(--color-primary)"
            stroke="var(--bg-primary)"
            strokeWidth="2"
          />
        ))}
        <text
          x={points[0].x}
          y={H - 4}
          textAnchor="middle"
          fontSize="9"
          fill="var(--text-secondary)"
        >
          {points[0].date.toLocaleDateString()}
        </text>
        <text
          x={points[points.length - 1].x}
          y={H - 4}
          textAnchor="middle"
          fontSize="9"
          fill="var(--text-secondary)"
        >
          {points[points.length - 1].date.toLocaleDateString()}
        </text>
      </svg>
    </div>
  );
};

const ItemPriceStats = ({
  item,
  cityName,
  cityId,
  history,
  loading,
  onClose,
}) => {
  if (!item) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "var(--bg-primary)",
          borderRadius: "0.75rem",
          width: "100%",
          maxWidth: "700px",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background:
              "linear-gradient(135deg, var(--color-primary), var(--color-info))",
            padding: "1.25rem 1.5rem",
            borderRadius: "0.75rem 0.75rem 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <div>
            <h3 style={{ color: "#fff", margin: 0, fontSize: "1.1rem" }}>
              📊 Price History
            </h3>
            <p
              style={{
                color: "rgba(255,255,255,0.85)",
                margin: "0.25rem 0 0",
                fontSize: "0.875rem",
              }}
            >
              {item.name} — {cityName || cityId}
            </p>
          </div>
          <button
            onClick={onClose}
            type="button"
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              color: "#fff",
              borderRadius: "50%",
              width: "32px",
              height: "32px",
              cursor: "pointer",
              fontSize: "1.125rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "1.5rem", flex: 1 }}>
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "var(--text-secondary)",
              }}
            >
              Loading history...
            </div>
          ) : history.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: "var(--text-secondary)",
              }}
            >
              No price history found for this item in {cityName || "your city"}.
            </div>
          ) : (
            <>
              {/* Table — oldest first, latest last */}
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  marginBottom: "2rem",
                  fontSize: "0.875rem",
                }}
              >
                <thead>
                  <tr style={{ background: "var(--bg-secondary)" }}>
                    {["S/N", "Price", "Unit", "Date"].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          padding: "0.625rem 0.75rem",
                          textAlign: i === 1 ? "right" : "left",
                          borderBottom: "2px solid var(--border-primary)",
                          color: "var(--text-secondary)",
                          fontWeight: "600",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((rec, idx) => {
                    const date = rec.created_at?.toDate
                      ? rec.created_at.toDate()
                      : new Date(rec.created_at);
                    return (
                      <tr
                        key={rec.id}
                        style={{
                          borderBottom: "1px solid var(--border-primary)",
                          background:
                            idx % 2 === 0
                              ? "transparent"
                              : "var(--bg-secondary)",
                        }}
                      >
                        <td
                          style={{
                            padding: "0.5rem 0.75rem",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {idx + 1}
                        </td>
                        <td
                          style={{
                            padding: "0.5rem 0.75rem",
                            textAlign: "right",
                            fontWeight: "500",
                            color: "var(--color-primary)",
                          }}
                        >
                          {rec.price}
                        </td>
                        <td
                          style={{
                            padding: "0.5rem 0.75rem",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {rec.unit || "—"}
                        </td>
                        <td
                          style={{
                            padding: "0.5rem 0.75rem",
                            color: "var(--text-secondary)",
                            fontSize: "0.8125rem",
                          }}
                        >
                          {date.toLocaleDateString()}{" "}
                          {date.toLocaleTimeString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Chart */}
              <PriceChart data={history} />
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ItemPriceStats;
